import React from 'react';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import clsx from 'clsx';
import { useController } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

type CheckboxRole = 'checkbox' | 'switch';

type CheckboxProps = {
  role: CheckboxRole;
  children?: ReactNode;
};

function Checkbox({ role, children }: CheckboxProps): ReactElement {
  return (
    <div className={clsx('form-check', { 'form-switch py-2': role === 'switch' })}>{children}</div>
  );
}

type InputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  control: Control<TFieldValues>;
  name: TName;
  role: CheckboxRole; // Required so that developers don't forget to add the role to switches.
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
};

function Input<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ id, control, name, role, disabled, onChange }: InputProps<TFieldValues, TName>): ReactElement {
  const { field } = useController({ control, name });
  return (
    <input
      {...field}
      id={id}
      className="form-check-input"
      type="checkbox"
      // Add role to more accurately convey the nature of the control to assistive technologies:
      role={role}
      checked={field.value}
      disabled={disabled}
      onChange={(value: ChangeEvent<HTMLInputElement>): void => {
        field.onChange(value);
        if (onChange) {
          onChange(value);
        }
      }}
    />
  );
}

type LabelProps = {
  htmlFor: string;
  children?: ReactNode;
};

function Label({ htmlFor, children }: LabelProps): ReactElement {
  return (
    <label htmlFor={htmlFor} className="form-check-label">
      {children}
    </label>
  );
}

/**
 * A checkbox/switch component that can be used with React Hook Form.
 *
 * Note: A Bootstrap 5 switch is essentially a styled checkbox. We can reuse the input with a
 * different role, the label without modification, while adding a different style to the outer
 * container div.
 */
export default Object.assign(Checkbox, {
  Input,
  Label
});
